import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Div, Paragraph, H3, H2, H4 } from '../Typography';
import { SkeletonText, Skeleton } from '@chakra-ui/react';

import CloseIconSVG from '../Icons/CloseIcon';
import StarIcon from '../Icons/Star';
import { useKeyPress } from '../../utils/useKeyPress';
import Breakpoints from '../../design-utils/breakpoints';
import { ImageCarousel } from './ImageCarousel';

const starColor = '#ffc600';

export interface SlidePanelProps {
  children?: JSX.Element | JSX.Element[];
  open: boolean;
  imgUrl?: string | null;
  price?: string;
  comparePrice?: string;
  description?: string;
  productName?: string;
  variantName?: string;
  subtitle?: string;
  onClose: () => void;
  isLoading?: boolean;
  isAddon?: boolean;
  hasOneVariant?: boolean;
  imgs?: string[];
  hidePrice?: boolean;
  reviews?: Reviews;
  id?: string | number;
  handle?: string;
  media?: Media[];
}

interface FocusableElement {
  focus(options?: FocusOptions): void;
}

export type FinalFocusRef = React.RefObject<FocusableElement>;

const SlidePanel: React.FC<SlidePanelProps> = ({
  children,
  open,
  imgUrl,
  imgs = [],
  price,
  comparePrice,
  description,
  productName,
  variantName,
  subtitle,
  onClose,
  isLoading = false,
  isAddon = false,
  hasOneVariant,
  hidePrice = false,
  reviews,
  id,
  handle,
  media = [],
}) => {
  const { t } = useTranslation();

  const escPressed = useKeyPress('Escape');
  const buttonRef = useRef<any>(null);

  useEffect(() => {
    escPressed && onClose();
  }, [escPressed]);

  useEffect(() => {
    if (open) {
      document.body.classList.add('subscription-overlay-open');
    } else {
      document.body.classList.remove('subscription-overlay-open');
    }
  }, [open]);

  const showPrice = () =>
    hidePrice ? null : (
      <Price>
        {comparePrice ? (
          <Paragraph
            modifiers={['body', 'subdued']}
            style={{ textDecoration: 'line-through' }}
          >
            {comparePrice}
          </Paragraph>
        ) : null}
        <Paragraph modifiers={{ body: true, emphasis: true }}>
          {price}
        </Paragraph>
      </Price>
    );

  useEffect(() => {
    if (open) {
      buttonRef.current?.focus();
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <SlidePanelBackdrop onClick={onClose} />
          <SlidePanelContainer
            className={
              isAddon
                ? 'slide-panel-container slide-panel-container--addon'
                : 'slide-panel-container'
            }
            data-testid="slide-panel"
            open={open}
            role="dialog"
            aria-modal="true"
            aria-labelledby="slidepanel-title"
            as={motion.div}
            initial={{ x: 336 }}
            transition={{ ease: [0.25, 0.46, 0.45, 0.94] }}
            animate={{
              x: 0,
            }}
            exit={{
              x: 336,
              transition: { duration: 0.5 },
            }}
          >
            <InfoWrapper>
              <SlidePanelHeader>
                <CloseButton
                  ref={buttonRef}
                  data-testid="slide-panel-close-button"
                  onClick={onClose}
                  aria-label={t('aria_labels.close_slide_panel')}
                >
                  <CloseIcon />
                </CloseButton>
              </SlidePanelHeader>
              {isLoading ? (
                <Skeleton height="323" borderRadius={6} />
              ) : hasOneVariant && imgs.length > 1 ? (
                <ImageCarousel
                  slides={imgs}
                  media={media}
                  productName={productName}
                />
              ) : imgUrl ? (
                <ImageContainer
                  src={imgUrl}
                  alt={productName}
                  data-testid="slider-panel-image"
                />
              ) : imgs?.length > 1 && hasOneVariant && open ? (
                <ImageCarousel
                  slides={imgs}
                  media={media}
                  productName={productName}
                />
              ) : (
                <ImageEmptyState data-testid="image-empty-state" />
              )}
              <ProductTitleContainer>
                {reviews?.average ? (
                  <Reviews reviews={reviews} handle={handle} productId={id} />
                ) : null}
                <H2 modifiers={['emphasis', 'body']} id="slidepanel-title">
                  {productName}
                </H2>
                <H3 modifiers={['body', 'subdued']}>{variantName}</H3>
                {price ? showPrice() : null}
                {subtitle ? (
                  <H3 modifiers={['body', 'subdued']}>{subtitle}</H3>
                ) : null}
              </ProductTitleContainer>
              <Divider />
              {isLoading ? (
                <SkeletonText mt={12} noOfLines={10} spacing="8" />
              ) : (
                <>
                  <H4 modifiers={['heading']} style={{ marginBottom: '10px' }}>
                    {t('titles.description')}
                  </H4>
                  <Div
                    dangerouslySetInnerHTML={{ __html: `${description || ''}` }}
                    modifiers={['body']}
                  ></Div>
                </>
              )}
            </InfoWrapper>
            {children}
          </SlidePanelContainer>
        </>
      )}
    </AnimatePresence>
  );
};

export default SlidePanel;

const StarsContainer = styled.div.attrs({
  className: 'bundle-product-card__stars-container',
})`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5rem auto;
  font-size: 1.7rem;
`;

const ReviewsCount = styled.p.attrs({
  className: 'bundle-product-card__reviews-count',
})`
  font-size: 1.2rem;
  color: var(--grey);
  text-decoration: underline;
`;

const ReviewsContainer = styled.div.attrs({
  className: 'bundle-product-card__reviews-container',
})`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Reviews = ({
  reviews,
  productId,
  handle,
}: {
  reviews: Reviews;
  productId?: string | number;
  handle?: string;
}) => {
  const { t } = useTranslation();
  const stars = Array.from(
    { length: Math.ceil(Number(reviews?.average) || 0) },
    (_, i) => i + 1,
  );

  const link = handle ? `/products/${handle}#${productId}` : null;

  return (
    <ReviewsContainer>
      <StarsContainer
        role="button"
        aria-label={t('aria_labels.stars_number', { stars_qty: stars.length })}
      >
        {stars.map((star) => (
          <StarIcon fill={starColor} key={star}></StarIcon>
        ))}
      </StarsContainer>
      {reviews.count ? (
        <ReviewsCount>
          (
          {link ? (
            <a href={link}>
              {reviews.count} {t('bundles.reviews')}
            </a>
          ) : (
            <span>
              {reviews.count} {t('bundles.reviews')}
            </span>
          )}
          )
        </ReviewsCount>
      ) : null}
    </ReviewsContainer>
  );
};

const ImageContainer = styled.img.attrs({
  className: 'bundle-img-container',
})`
  display: inline-block;
  width: 100%;
  margin-top: 15px;
  border-radius: 8px;
`;

interface SlidePanelContainerProps {
  readonly open?: boolean;
}

const SlidePanelContainer = styled.div<SlidePanelContainerProps>`
  height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  background-color: #ffffff;
  z-index: 10100;
  display: flex;
  flex-direction: column;
  @media screen and (min-width: ${Breakpoints.sm}) {
    width: 336px;
  }
  @media screen and (max-width: ${Breakpoints.lg}) {
    width: 100%;
  }
  width: 336px;
  *:empty {
    display: block;
  }
`;

const Price = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: baseline;
`;

const SlidePanelHeader = styled.div.attrs({
  className: 'slide-panel-header',
})`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const SlidePanelBackdrop = styled.div.attrs({
  className: 'slide-panel-backdrop',
})`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(89, 89, 94, 0.5);
  z-index: 10050;
  &:empty {
    display: block;
  }
`;

const CloseIcon = styled(CloseIconSVG).attrs({
  className: 'bundle-close-icon--slide-panel-close',
})`
  width: 24px;
  height: 24px;
`;

const CloseButton = styled.button.attrs({
  className: 'bundle-close-button',
})`
  background-color: transparent;
  border-radius: 50%;
  border: none;
`;

const ProductTitleContainer = styled.div.attrs({
  className: 'bundle-slideout-title-container',
})`
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  margin-top: 12px;

  > p {
    margin-bottom: 8px;
  }

  > p + p {
    margin-bottom: 22px;
  }
`;

const Divider = styled.hr`
  border: 1px solid #f1f2f3;
  margin: 24px 0;
`;

const ImageEmptyState = styled.div.attrs({
  className: 'bundle-img-empty-state',
})`
  width: 280px;
  height: 323px;
  border-radius: 8px;
  background-color: #dfe3e8;
  position: relative;
  &:empty {
    display: block;
  }
`;

const InfoWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 28px;
  width: 100%;
`;
